import React, {useState} from "react";
import {useSelector} from "react-redux";

import LoadingComponent from "../../components/LoadingComponent";
import {useTranslation} from "react-i18next";
import {Employee} from "../../app/interfaces";
import {useNavigate} from "react-router-dom";
import useFetchEmployees from "../../hooks/useFetchEmployees";
import CountryFlagIcon from "../../components/CountryFlagIcon";
import {countryNameByCode} from "../../utils/countryCodes";
import {fullNameForUser, isValidString} from "../../utils";
import {Table, TableBody, TableHeader, TableNavigation} from "../../components/Table";
import {
    TableDataCell,
    TableRow,
    TableHeaderCell
} from "../../components/Table/styles";
import DataTableLayout, {EmptyDataTableLayout} from "../../layouts/DataTableLayout/DataTableLayout";
import {
    DataTableLayoutFooter,
    DataTableLayoutHeader,
    DataTableLayoutTitle,
} from "../../layouts/DataTableLayout/styles";
import {EmployeeAddress, EmployeeIdentification, EmployeeCountry} from "./styles";
import {employeesPendingActionsStatuses, EOR_SALARY_CHANGE_STATUS_REQUESTED} from "../../utils/constants";
import defaultAvatarImage from "../../assets/images/avatar.svg";
import {FlexColumn, StatusPill} from "../../components";
import {allEmployeesFetchError, allEmployeesFetchState, hasPendingSalaryChange, selectedEmployeesPaginatedWithSalaryChange} from "../../app/employee/selectors";

interface PendingRegistrationsProps {
    itemsPerPage: number;
}

const PendingEmployees = ({itemsPerPage}: PendingRegistrationsProps) => {

    useFetchEmployees()

    const [currentPage, setCurrentPage] = useState<number>(1)

    const employees = useSelector(
        selectedEmployeesPaginatedWithSalaryChange(
            currentPage,
            itemsPerPage,
            employeesPendingActionsStatuses,
            null
        )
    );

    const fetchState = useSelector(allEmployeesFetchState)
    const error = useSelector(allEmployeesFetchError)

    const navigate = useNavigate();

    const {t} = useTranslation();

    const dataTableTitle = t('employees_pending_actions')

    const handleOpenEmployee = (employee: Employee) => navigate(`/employee/${employee._id}`);

    if (fetchState === 'loading' || fetchState === 'idle') {
        return <EmptyDataTableLayout title={dataTableTitle}>
            <LoadingComponent/>
        </EmptyDataTableLayout>
    }

    if (error) {
        console.warn(error)
        return <EmptyDataTableLayout title={dataTableTitle}>
            {error}
        </EmptyDataTableLayout>
    }

    if (!employees.items.length) {
        return <EmptyDataTableLayout title={dataTableTitle}>
            {t('no_employees_pending_actions') as string}
        </EmptyDataTableLayout>
    }

    return (
        <DataTableLayout>
            <DataTableLayoutHeader>
                <DataTableLayoutTitle>{dataTableTitle}</DataTableLayoutTitle>
            </DataTableLayoutHeader>
            <Table>
                <TableHeader>
                    <TableHeaderCell>{t('employee')}</TableHeaderCell>
                    <TableHeaderCell>{t('location')}</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'center' }}>
                        {t('status')}
                    </TableHeaderCell>
                </TableHeader>
                <TableBody>
                    {employees.items.map((e, i) => EmployeeRow(e, i, t, handleOpenEmployee))}
                </TableBody>
            </Table>
            <DataTableLayoutFooter>
                <TableNavigation
                    pageLimit={itemsPerPage}
                    summary={employees.summary}
                    setPage={setCurrentPage}
                />
            </DataTableLayoutFooter>
        </DataTableLayout>
    );
}

const EmployeeRow = (employee: Employee, index: number, t:Function, handleOpenEmployee: Function) => {
    return <TableRow key={index} clickable onClick={() => handleOpenEmployee(employee)}>
        <TableDataCell>
            <EmployeeIdentification>
                {/*@ts-ignore*/}
                <img alt="avatar" src={isValidString(employee?.avatar) ? `${process.env.REACT_APP_API_URL}/${employee.avatar}` : defaultAvatarImage}/>
                <FlexColumn>
                    <h3>{fullNameForUser(employee)}</h3>
                    <h4>{employee.activity}</h4>
                </FlexColumn>
            </EmployeeIdentification>
        </TableDataCell>
        <TableDataCell>
            <EmployeeAddress>
                <h3>{employee.address}</h3>
                <EmployeeCountry
                    countryName={countryNameByCode(employee.tax_residence) as string}
                    countryCode={employee.tax_residence}>
                    <CountryFlagIcon countryCode={employee.tax_residence}/>
                    {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                    <h6></h6>
                </EmployeeCountry>
            </EmployeeAddress>
        </TableDataCell>
        <TableDataCell style={{textAlign: "center"}}>
            <FlexColumn align="center" justify="center" style={{height: '100%'}}>
                {!employeesPendingActionsStatuses.includes(employee.status) && hasPendingSalaryChange(employee) ? (
                    <StatusPill status={EOR_SALARY_CHANGE_STATUS_REQUESTED}>
                        {t('salary_change_requested')}
                    </StatusPill>
                ) : (
                    <StatusPill status={employee.status}>
                        {t(employee.status)}
                    </StatusPill>
                )}
            </FlexColumn>
        </TableDataCell>
    </TableRow>
}

export default PendingEmployees;
